import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { Divider } from "@msuite/ui/divider";
import { useStream } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { useCitadel } from "@/hooks/use-citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";

/** Props Interface */
interface ProjectSummaryProps {
	projectId: string;
}

export function ProjectSummary({ projectId }: ProjectSummaryProps) {
	/** Hooks */
	const { t } = useTranslation();
	const stream = useStream(`${process.env.PUBLIC_SERVER_URL}/projects/${projectId}/summary`, {
		startStreamOnMount: false,
		resetDataOnInput: false,
	});

	/** Query */
	const { citadel } = useCitadel();
	const { project } = useProject({ projectId });

	const { data: projectSummary, isFetching } = useQuery({
		queryKey: ["projectSummary", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].summary.$get({
				param: { projectId },
			});
			const data = await responseHandler(response);
			return data.summary;
		},
	});

	/** Computed */
	// Generating a summary replaces the text everyone else reads on the project page and spends a
	// language-model request doing it, so it asks the same rule as any other change to the site —
	// and asks it about this site, because `update Project` reaches the organisation, the sites its
	// holder runs or the ones they created. Same three-state guard as `Can` documents: the control
	// stays while the rules or the project are still loading, and a denial decides.
	const generateIsDenied =
		!!citadel && !!project && citadel.cannot("update", "Project", { ...project });

	/** Render */
	return (
		<ContentCard
			title={t("projects.project_summary")}
			icon={icon("LetterText")}
		>
			{isFetching ? (
				<BlankSlate
					onClick={() => {}}
					isLoading
					className="h-[150px]!"
					withAccessory={false}
					withBorder={false}
					withBackground={false}
				/>
			) : (
				<div className="v-stack">
					{!projectSummary && !stream.data ? (
						<BlankSlate
							onClick={() => {
								if (generateIsDenied) return;
								stream.startStream({});
							}}
							Icon={icon("LetterText")}
							isLoading={stream.isLoading}
							withAccessory={false}
							withBorder={false}
							withBackground={false}
							label={t("projects.click_here_to_generate_project_summary")}
							className="h-[150px]!"
						/>
					) : stream.data ? (
						<p className="whitespace-pre-wrap">{stream.data}</p>
					) : (
						<p className="whitespace-pre-wrap">{projectSummary?.summary}</p>
					)}
					{/* The server has the last word on the rule, so a refusal has to be visible here
					    rather than leaving the card exactly as it was before the click. */}
					{stream.error && <p className="label text-red-500">{t("general.request_failed")}</p>}
					<Divider variant="content-card" />
					<div className="h-stack justify-between -mb-2 -mr-2">
						<p className="label">{moment(projectSummary?.createdAt).longFormat()}</p>
						{!generateIsDenied && (
							<Button
								size="xxs"
								variant="ghost"
								icon={<Icon name="Brain" />}
								isLoading={stream.isLoading}
								onClick={() => {
									stream.startStream({});
								}}
							>
								{t("projects.regenerate_project_summary")}
							</Button>
						)}
					</div>
				</div>
			)}
		</ContentCard>
	);
}
